import { Component, Input, OnInit } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AbstractControl } from '@angular/forms';
import { finalize, forkJoin, Observable, of, ReplaySubject, takeUntil, tap } from 'rxjs';
import { ICountry } from '../../types/country-code.interface';
import { catchError } from 'rxjs/operators';
import { IIdValue } from '../../types/id-value.interface';
import { CountriesService } from 'core/services/countries.service';
import { KorviuSelectComponent } from '../korviu-select/korviu-select.component';

const mxCountryId: number = 145;
const usCountryId: number = 240;

@Component({
  selector: 'app-country-select',
  standalone: true,
  imports: [NzSpinModule, TranslateModule, KorviuSelectComponent],
  templateUrl: './country-select.component.html',
  styleUrl: './country-select.component.scss'
})
export class CountrySelectComponent implements OnInit {
  @Input() public control: AbstractControl;

  public countriesIsLoading: boolean;
  public countriesMap: Map<string, IIdValue[]>;

  public get countries(): IIdValue[] {
    return this.countriesMap.get(this._translateService.currentLang);
  }

  private readonly _onDestroy$: ReplaySubject<void>;

  constructor(
    private _translateService: TranslateService,
    private _countriesService: CountriesService
  ) {
    this.countriesIsLoading = false;
    this.countriesMap = new Map<string, IIdValue[]>();
    this._onDestroy$ = new ReplaySubject(1);
  }

  public ngOnInit(): void {
    this._loadCountries();
  }

  private _loadCountries(): void {
    this.countriesIsLoading = true;
    forkJoin([this._getCountriesEn(), this._getCountriesEs()])
      .pipe(
        finalize(() => {
          this.countriesIsLoading = false;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  private _getCountriesEn(): Observable<ICountry[]> {
    return this._countriesService.getCountries('en').pipe(
      catchError(() => of([])),
      tap((countries: ICountry[]) => {
        this.countriesMap.set('en', this._getCountriesAsIIdValue(this._sortCountries(countries)));
      })
    );
  }

  private _getCountriesEs(): Observable<ICountry[]> {
    return this._countriesService.getCountries('es').pipe(
      catchError(() => of([])),
      tap((countries: ICountry[]) => {
        this.countriesMap.set('es', this._getCountriesAsIIdValue(this._sortCountries(countries)));
      })
    );
  }

  private _getCountriesAsIIdValue(countries: ICountry[]): IIdValue[] {
    return countries.map((country: ICountry) => {
      return { id: country.id, value: country.name, countryCode: country.code };
    });
  }

  private _sortCountries(countriesResponse: ICountry[]): ICountry[] {
    const countries: ICountry[] = [];
    const mxCountry: ICountry = countriesResponse.find((country: ICountry) => country.id === mxCountryId);
    const usCountry: ICountry = countriesResponse.find((country: ICountry) => country.id === usCountryId);
    const filteredCountries = countriesResponse.filter((country: ICountry) => {
      return country.id !== mxCountryId && country.id !== usCountryId;
    });
    if (mxCountry) {
      countries.push(mxCountry);
    }
    if (usCountry) {
      countries.push(usCountry);
    }
    countries.push(...filteredCountries);

    return countries;
  }
}
